import { useCallback, useMemo, useState } from 'react';
import { useFetch } from '../../../hooks/useFetch';
import { useNotification } from '../../notifications/hooks/useNotification';
import { RightsProfilesApiFactory } from '../api/rights-profiles.api';
import { ProfileInUseError } from '../errors/profile-in-use.error';
import { ProfileNameConflictError } from '../errors/profile-conflict.error';
import type { RightsProfile, CreateRightsProfilePayload } from '../types/rights-profile.types';

/**
 * Outcome of a delete attempt. `in-use` is a normal business result (the profile
 * is still assigned to users), NOT an exception — so `DeleteProfileDialog`
 * switches on `status` and shows the blocking message with the name + count,
 * instead of catching an error. `error` covers any unexpected failure (a toast is
 * already raised by the hook).
 */
export type RemoveOutcome =
  | { status: 'deleted' }
  | { status: 'in-use'; assignedUsersCount: number; profileName: string }
  | { status: 'error' };

export interface UseRightsProfilesResult {
  saving: boolean;
  create: (payload: CreateRightsProfilePayload) => Promise<RightsProfile | null>;
  update: (
    id: string,
    payload: Partial<CreateRightsProfilePayload>,
  ) => Promise<RightsProfile | null>;
  duplicate: (id: string) => Promise<RightsProfile | null>;
  removingId: string | null;
  remove: (profile: RightsProfile) => Promise<RemoveOutcome>;
}

/**
 * The rights-profiles mutations (`create` / `update` / `duplicate` / `remove`) and
 * their pending state. It loads nothing (HOMCO-1413): the list is read through
 * `useGetRightsProfiles`, so the Create and Edit pages no longer fetch it on mount.
 * Success toasts + navigation stay in the pages (they own the i18n and routing);
 * the hook only raises an error toast for unexpected failures.
 */
export function useRightsProfiles(): UseRightsProfilesResult {
  const [client] = useFetch();
  const api = useMemo(() => RightsProfilesApiFactory(client), [client]);
  const { notifyError } = useNotification();

  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const create = useCallback(
    async (payload: CreateRightsProfilePayload): Promise<RightsProfile | null> => {
      setSaving(true);
      try {
        return await api.create(payload);
      } catch (err) {
        // A name conflict is a form-field concern (shown on the Name field by the
        // caller, inputs preserved) — rethrow it instead of raising a global toast.
        if (ProfileNameConflictError.is(err)) throw err;
        notifyError(err);
        return null;
      } finally {
        setSaving(false);
      }
    },
    [api, notifyError],
  );

  const update = useCallback(
    async (
      id: string,
      payload: Partial<CreateRightsProfilePayload>,
    ): Promise<RightsProfile | null> => {
      setSaving(true);
      try {
        return await api.update(id, payload);
      } catch (err) {
        // A name conflict is shown on the Name field by the caller (like create),
        // inputs preserved — rethrow it instead of a global toast.
        if (ProfileNameConflictError.is(err)) throw err;
        notifyError(err);
        return null;
      } finally {
        setSaving(false);
      }
    },
    [api, notifyError],
  );

  const duplicate = useCallback(
    async (id: string): Promise<RightsProfile | null> => {
      setSaving(true);
      try {
        return await api.duplicate(id);
      } catch (err) {
        notifyError(err);
        return null;
      } finally {
        setSaving(false);
      }
    },
    [api, notifyError],
  );

  const remove = useCallback(
    async (profile: RightsProfile): Promise<RemoveOutcome> => {
      setRemovingId(profile.id);
      try {
        await api.remove(profile.id);
        return { status: 'deleted' };
      } catch (err) {
        if (ProfileInUseError.is(err)) {
          // Business outcome, not an error toast — the dialog shows the message.
          // Fall back to the row's own count/name if the 409 message didn't parse.
          return {
            status: 'in-use',
            assignedUsersCount: err.assignedUsersCount ?? profile.assigned_users_count,
            profileName: err.profileName ?? profile.name,
          };
        }
        notifyError(err);
        return { status: 'error' };
      } finally {
        setRemovingId(null);
      }
    },
    [api, notifyError],
  );

  return {
    saving,
    create,
    update,
    duplicate,
    removingId,
    remove,
  };
}
